import clsx from "clsx";
import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { HyphenIcon } from "@transfr-inc/dashboard-components";
import {
  BasicCell,
  Column,
  ColumnOptions,
  ColumnType,
  DataTable,
  PercentageDataCell,
  SortDirection,
  TimeDataCell,
} from "@transfr-inc/dashboard-components/data-table";
import { formatDateDigits } from "@transfr-inc/dashboard-components/utils";

import { ActivityStatusCell } from "@components/table-cells/status-cell";
import {
  DataTableColumnDefinition,
  getUserDetailPathConfig,
} from "@views/users/list/data-table-column-definition";

import { MasteryStatus } from "@models";

import "./classroom.mastery-table.scss";

export function ClassroomMasteryTable({ data, responsiveStyles }) {
  const { classroomId } = useParams();
  const breadcrumbLabel = "Classroom";
  const tableData = useMemo(
    () =>
      data?.map((row) => ({
        ...row,
        masteryStatus: row.masteryStatus ?? row.status,
        status: row.masteryStatus ?? row.status,
      })) ?? [],
    [data]
  );
  const columns = [
    DataTableColumnDefinition.AVATAR,
    DataTableColumnDefinition.LAST_NAME(
      getUserDetailPathConfig({ breadcrumbLabel, classroomId })
    ),
    DataTableColumnDefinition.FIRST_NAME(
      getUserDetailPathConfig({ breadcrumbLabel, classroomId })
    ),
    DataTableColumnDefinition.USERNAME,
    new Column(
      "timeSpent",
      "Time",
      ColumnType.CUSTOM,
      new ColumnOptions({ sortingEnabled: true, customFunction: TimeDataCell })
    ),
    new Column(
      "status",
      "Status",
      ColumnType.CUSTOM,
      new ColumnOptions({
        sortingEnabled: true,
        customFunction: (value, row) => {
          if (!value) return <BasicCell />;
          return (
            <div>
              <ActivityStatusCell status={value} row={row} />
            </div>
          );
        },
      })
    ),
    new Column(
      "mastery",
      "Score",
      ColumnType.CUSTOM,
      new ColumnOptions({
        sortingEnabled: true,
        customFunction: (value, row) => {
          if (!row?.displayScore) {
            return <div className="mastery-cell">N/A</div>;
          }
          const alwaysShowScore =
            row.status !== MasteryStatus.NotStarted && row.status;
          return PercentageDataCell(value, "truncate", alwaysShowScore);
        },
      })
    ),
    new Column(
      "attempts",
      "Attempts",
      ColumnType.COUNT,
      new ColumnOptions({ sortingEnabled: true })
    ),
    new Column(
      "latestTimeStamp",
      "Last Attempted",
      ColumnType.CUSTOM,
      new ColumnOptions({
        sortingEnabled: true,
        customFunction: (value) => {
          const formattedDate = value ? formatDateDigits(value) : undefined;
          return <BasicCell value={formattedDate}></BasicCell>;
        },
      })
    ),
    new Column(
      "completed",
      "Complete",
      ColumnType.CUSTOM,
      new ColumnOptions({
        sortingEnabled: true,
        sortKeyFunctions: [(row) => row.simCompletionDate ?? ""],
        customFunction: (_value, row) => {
          const completed = row.completed;
          const formattedDate = row.simCompletionDate
            ? formatDateDigits(row.simCompletionDate)
            : undefined;
          return (
            <div className="completed-cell">
              {completed ? (
                <>
                  <FontAwesomeIcon icon="fa-solid fa-circle-check"></FontAwesomeIcon>
                  {formattedDate}
                </>
              ) : (
                <BasicCell />
              )}
            </div>
          );
        },
      })
    ),
  ];

  return (
    <DataTable
      className={clsx("classroom-mastery-table", responsiveStyles)}
      columns={columns}
      data={tableData}
      keyFields={["userId"]}
      defaultSortPropName={"latestTimeStamp"}
      defaultSortDirection={SortDirection.DESCENDING}
      responsiveStyles={responsiveStyles}
      rowHeightConfig={{ large: 54, medium: 160, small: 280 }}
    ></DataTable>
  );
}
